"use client";
import { useSpring, animated, useInView, useSprings } from '@react-spring/web';
import { Target, Brain, Briefcase, Rocket, CheckCircle, X } from 'lucide-react';

export default function AudienceSection() {
  const audiences = [
    {
      icon: Target,
      title: "COACHES Y MENTORES",
      subtitle: "Que quieren pasar al siguiente nivel",
      checklist: [
        "Ya tienes clientes pero sientes que podrías dar más",
        "Quieres diferenciarte con una metodología única",
        "Buscas certificación ICF reconocida"
      ]
    },
    {
      icon: Brain,
      title: "PSICÓLOGOS Y TERAPEUTAS",
      subtitle: "Que buscan herramientas transformadoras",
      checklist: [
        "Quieres resultados más rápidos con tus pacientes",
        "Buscas un enfoque práctico y estructurado",
        "Necesitas renovar tu práctica profesional"
      ]
    },
    {
      icon: Briefcase,
      title: "LÍDERES Y CONSULTORES",
      subtitle: "Que quieren impactar en organizaciones",
      checklist: [
        "Trabajas con equipos y buscas nuevas dinámicas",
        "Quieres facilitar transformaciones culturales",
        "Necesitas herramientas de alto impacto"
      ]
    },
    {
      icon: Rocket,
      title: "EMPRENDEDORES Y PROFESIONALES",
      subtitle: "Que quieren alinear negocio y valores",
      checklist: [
        "Sientes que tu negocio debe tener más sentido",
        "Quieres liderar desde la coherencia",
        "Buscas tu factor diferencial único"
      ]
    }
  ];

  const notForYou = [
    "Buscas otro título más para tu colección",
    "Quieres recetas mágicas sin hacer el trabajo interno",
    "No estás dispuesto a cuestionar tus propias creencias",
    "Solo te interesa el diploma, no la transformación"
  ];

  // Header animation
  const headerSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: 400 }
  });

  // Audience cards animations
  const [audienceRef, audienceInView] = useInView({ once: true, rootMargin: '-80px' });
  const audienceSprings = useSprings(
    audiences.length,
    audiences.map((_, index) => ({
      opacity: audienceInView ? 1 : 0,
      transform: audienceInView ? 'translateY(0px)' : 'translateY(20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Question section
  const [questionRef, questionInView] = useInView({ once: true, rootMargin: '-50px' });
  const questionSpring = useSpring({
    opacity: questionInView ? 1 : 0,
    transform: questionInView ? 'scale(1)' : 'scale(0.95)',
    config: { duration: 400 }
  });

  // Not for you section
  const [notRef, notInView] = useInView({ once: true, rootMargin: '-50px' });
  const notSpring = useSpring({
    opacity: notInView ? 1 : 0,
    config: { duration: 400 }
  });

  // Not for you items
  const [notItemsRef, notItemsInView] = useInView({ once: true, rootMargin: '-80px' });
  const notItemsSprings = useSprings(
    notForYou.length,
    notForYou.map((_, index) => ({
      opacity: notItemsInView ? 1 : 0,
      transform: notItemsInView ? 'translateX(0px)' : 'translateX(-20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Final CTA
  const [ctaRef, ctaInView] = useInView({ once: true, rootMargin: '-80px' });
  const ctaSpring = useSpring({
    opacity: ctaInView ? 1 : 0,
    transform: ctaInView ? 'translateY(0px)' : 'translateY(20px)',
    config: { duration: 400 }
  });

  return (
    <section id="audience" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Provocative headline */}
        <animated.div
          className="text-center mb-16"
          style={headerSpring}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-tight text-black mb-6">
            NO es para cualquiera
            <span className="block text-[#97d700]">Es para ti si...</span>
          </h2>
        </animated.div>

        {/* Audience cards */}
        <div ref={audienceRef} className="grid md:grid-cols-2 gap-8 mb-16">
          {audienceSprings.map((style, index) => {
            const audience = audiences[index];
            return (
              <animated.div
                key={index}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-shadow duration-300"
                style={style}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#97d700]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <audience.icon className="w-6 h-6 text-[#97d700]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading uppercase tracking-wider text-black mb-1">
                      {audience.title}
                    </h3>
                    <p className="text-gray-600">{audience.subtitle}</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {audience.checklist.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle className="w-4 h-4 text-[#97d700] mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </animated.div>
            );
          })}
        </div>

        {/* The real question */}
        <animated.div
          ref={questionRef}
          className="bg-gradient-to-r from-[#97d700] to-[#7ab300] text-white p-8 md:p-12 rounded-3xl mb-16"
          style={questionSpring}
        >
              <div className="max-w-4xl mx-auto text-center">
                <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-tight mb-6">
                  La pregunta real no es si cumples el perfil
                </h3>
                <p className="text-xl md:text-2xl mb-8">
                  Es si sabes que tienes más que ofrecer y ha llegado la hora de demostrarlo para
                  <span className="block font-bold text-2xl md:text-3xl mt-2">
                    CONVERTIRTE EN EL PROFESIONAL QUE SABES QUE PUEDES SER
                  </span>
                </p>
                <div className="inline-flex items-center gap-2  px-6 py-3 ">
                  <span className="w-3 h-3 bg-white rounded-full animate-pulse"></span>
                  <span className="font-medium">Solo para valientes que quieren brillar</span>
                </div>
              </div>
        </animated.div>

        {/* NOT for you section */}
        <animated.div
          ref={notRef}
          className="bg-black text-white p-8 md:p-12 rounded-3xl"
          style={notSpring}
        >
              <div className="max-w-3xl mx-auto">
                <h3 className="text-2xl md:text-3xl font-heading uppercase tracking-wider text-[#97d700] mb-8 text-center">
                  Y definitivamente NO es para ti si...
                </h3>
                <div ref={notItemsRef} className="grid md:grid-cols-2 gap-6">
                  {notItemsSprings.map((style, index) => {
                    const item = notForYou[index];
                    return (
                      <animated.div
                        key={index}
                        style={style}
                        className="flex items-start gap-3"
                      >
                        <X className="w-7 h-7 text-red-500 mt-1 flex-shrink-0" strokeWidth={4} />
                        <p className="text-gray-300">{item}</p>
                      </animated.div>
                    );
                  })}
                </div>
                <p className="text-center mt-8 text-xl text-gray-400">
                  Esto es para profesionales comprometidos con la excelencia
                </p>
              </div>
        </animated.div>

        {/* Final CTA */}
        <animated.div
          ref={ctaRef}
          className="text-center mt-16"
          style={ctaSpring}
        >
              <p className="text-2xl md:text-3xl font-heading uppercase tracking-wider text-black mb-2">
                ¿Sientes que esto es para ti?
              </p>
              <p className="text-xl text-gray-600 mb-8">
                Confía en esa sensación. Tu intuición nunca miente.
              </p>
              <a
                href="#registration"
                className="inline-block px-10 py-5 bg-[#97d700] text-black font-heading uppercase tracking-wider font-bold text-xl rounded-full shadow-2xl hover:shadow-[#97d700]/30 transform hover:scale-105 transition-all duration-300"
                aria-label="Sí, este es mi momento - navegar a la sección de registro"
              >
                SÍ, ESTE ES MI MOMENTO
              </a>
        </animated.div>
      </div>
    </section>
  );
}